// Institutional Documents Service - Manages establishment documents with version history

import { apiGet, apiPatch, apiDelete, apiPostMultipart, apiFetchBlob, apiDownloadFile } from '@/utils/api';

export enum InstitutionalDocumentType {
  REGLEMENT_INTERIEUR_ETUDIANTS = 'REGLEMENT_INTERIEUR_ETUDIANTS',
  REGLEMENT_INTERIEUR_STAFF = 'REGLEMENT_INTERIEUR_STAFF',
  CONSENTEMENT_MEDIAS = 'CONSENTEMENT_MEDIAS',
  CONVENTION_STAGE = 'CONVENTION_STAGE',
}

export const DOCUMENT_TYPE_LABELS: Record<InstitutionalDocumentType, string> = {
  [InstitutionalDocumentType.REGLEMENT_INTERIEUR_ETUDIANTS]: 'Règlement intérieur - Étudiants',
  [InstitutionalDocumentType.REGLEMENT_INTERIEUR_STAFF]: 'Règlement intérieur - Staff',
  [InstitutionalDocumentType.CONSENTEMENT_MEDIAS]: 'Consentement médias et réseaux sociaux',
  [InstitutionalDocumentType.CONVENTION_STAGE]: 'Convention de Stage (Template)',
};

export const DOCUMENT_TYPE_DESCRIPTIONS: Record<InstitutionalDocumentType, string> = {
  [InstitutionalDocumentType.REGLEMENT_INTERIEUR_ETUDIANTS]: 'Document définissant les règles de conduite et les droits des étudiants au sein de l\'établissement.',
  [InstitutionalDocumentType.REGLEMENT_INTERIEUR_STAFF]: 'Document définissant les règles de conduite et les droits du personnel de l\'établissement.',
  [InstitutionalDocumentType.CONSENTEMENT_MEDIAS]: 'Formulaire de consentement pour l\'utilisation des images et vidéos sur les médias et réseaux sociaux.',
  [InstitutionalDocumentType.CONVENTION_STAGE]: 'Template de convention de stage à télécharger, remplir et retourner signé.',
};

export interface InstitutionalDocumentVersion {
  id: string;
  versionNumber: number;
  fileName: string;
  originalFileName: string;
  fileUrl: string;
  fileSize: number;
  mimeType: string;
  uploadedBy: string;
  uploadedByName: string;
  notes?: string;
  isCurrentVersion: boolean;
  createdAt: string;
}

export interface InstitutionalDocument {
  id: string;
  documentType: InstitutionalDocumentType;
  label: string;
  isActive: boolean;
  currentVersion?: InstitutionalDocumentVersion;
  versions: InstitutionalDocumentVersion[];
  createdAt: string;
  updatedAt: string;
}

export interface UploadResponse {
  success: boolean;
  message: string;
  data: {
    document: {
      id: string;
      documentType: InstitutionalDocumentType;
      label: string;
    };
    version: InstitutionalDocumentVersion;
  };
}

/**
 * Get all institutional documents for the establishment
 */
export async function fetchInstitutionalDocuments(): Promise<InstitutionalDocument[]> {
  return apiGet<InstitutionalDocument[]>('/institutional-documents');
}

/**
 * Get a specific institutional document with all versions
 */
export async function fetchInstitutionalDocument(
  documentType: InstitutionalDocumentType,
): Promise<InstitutionalDocument> {
  return apiGet<InstitutionalDocument>(`/institutional-documents/${documentType}`);
}

/**
 * Upload a new version of an institutional document
 */
export async function uploadInstitutionalDocument(
  documentType: InstitutionalDocumentType,
  file: File,
  notes?: string,
): Promise<UploadResponse> {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('documentType', documentType);
  if (notes) {
    formData.append('notes', notes);
  }

  return apiPostMultipart<UploadResponse>('/institutional-documents/upload', formData);
}

/**
 * Set a specific version as the current version
 */
export async function setCurrentVersion(
  documentId: string,
  versionId: string,
): Promise<{ success: boolean; message: string }> {
  return apiPatch<{ success: boolean; message: string }>(
    `/institutional-documents/${documentId}/versions/${versionId}/set-current`,
  );
}

/**
 * Delete a specific version
 */
export async function deleteVersion(
  documentId: string,
  versionId: string,
): Promise<{ success: boolean; message: string }> {
  return apiDelete<{ success: boolean; message: string }>(
    `/institutional-documents/${documentId}/versions/${versionId}`,
  );
}

/**
 * Get the endpoint path for downloading a specific version
 */
export function getDownloadEndpoint(documentId: string, versionId: string): string {
  return `/institutional-documents/${documentId}/versions/${versionId}/download`;
}

/**
 * Get the endpoint path for previewing a specific version
 */
export function getPreviewEndpoint(documentId: string, versionId: string): string {
  return `/institutional-documents/${documentId}/versions/${versionId}/preview`;
}

/**
 * Securely download a document version using Authorization header
 */
export async function downloadDocumentVersion(
  documentId: string,
  versionId: string,
  filename: string,
): Promise<void> {
  const endpoint = getDownloadEndpoint(documentId, versionId);
  return apiDownloadFile(endpoint, filename);
}

/**
 * Securely fetch document preview as blob using Authorization header
 */
export async function fetchDocumentPreview(
  documentId: string,
  versionId: string,
): Promise<Blob> {
  const endpoint = getPreviewEndpoint(documentId, versionId);
  return apiFetchBlob(endpoint);
}

/**
 * Format file size for display
 */
export function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
}

/**
 * Format date for display
 */
export function formatDate(dateString: string): string {
  const date = new Date(dateString);
  return date.toLocaleDateString('fr-FR', {
    day: '2-digit',
    month: 'long',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}
